import axiosInstance from '@/axiosInstance';
import type { AxiosResponse } from 'axios';
import type { User, Role, LoginRequest, ImpersonationState, AssignableUser } from './types';

interface AuthenticationMethodResponse {
  authenticationMethod?: string | null;
}

interface AuthSessionResponse {
  token: string;
  expirationTS: string;
  errorMessage?: string;
}

export default {
  async getUsers(): Promise<AxiosResponse<User[]>> {
    try {
      const response = await axiosInstance.get(`/api/User/`);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getUserNames(): Promise<AxiosResponse<string[]>> {
    try {
      const response = await axiosInstance.get(`/api/User/GetUserNames`);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  // Assignee options for the WO list filter: active users holding write-workorder for `sites`.
  // Pass [] for the caller's whole site scope.
  //
  // `indexes: null` makes axios emit repeated `sites=A&sites=B`. Its default `sites[]=A` form does
  // NOT bind to the endpoint's `List<string> sites`, and would silently widen the result to the
  // caller's whole scope instead of erroring — so this serializer is load-bearing, not cosmetic.
  async getAssignableUsers(sites: string[]): Promise<AxiosResponse<AssignableUser[]>> {
    try {
      const response = await axiosInstance.get(`/api/User/GetAssignableUsers`, {
        params: { sites },
        paramsSerializer: { indexes: null },
      });
      return response;
    } catch (error) {
      console.error('Error fetching assignable users:', error);
      throw error;
    }
  },
  async getUserBySessionToken(sessionToken: string): Promise<AxiosResponse<User>> {
    try {
      const response = await axiosInstance.get(`/api/User/GetBySessionToken`, { params: { sessionToken: sessionToken } });
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getAuthMethod(emailAddress: string): Promise<AxiosResponse<AuthenticationMethodResponse>> {
    try {
        const response = await axiosInstance.get(`/api/User/GetUserAuthMethod`, { params: { emailAddress: emailAddress }});
        return response;
    } catch (error) {
        throw error;
    }
  },
  async loginWithEntraId(emailAddress: string, entraToken: string): Promise<AxiosResponse<AuthSessionResponse>> {
    try {
        const request = { email: emailAddress, password: entraToken } as LoginRequest;
        const response = await axiosInstance.post(`/api/User/LoginWithEntraID`, request);
        return response;
    } catch (error) {
        throw error;
    }
  },
  async loginWithPassword(emailAddress: string, password: string): Promise<AxiosResponse<AuthSessionResponse>>{
    try {
      const request = { email: emailAddress, password: password } as LoginRequest;
      const response = await axiosInstance.post(`/api/User/Login`, request);
      return response;
    } catch (error) {
      throw error;
    }
  },
  async extendSession(): Promise<AxiosResponse<string>> {
    try {
      const response = await axiosInstance.get(`/api/User/ExtendSession`);
      return response;
    } catch (error) {
      console.error('Error: ', error);
      throw error;
    }
  },
  async logout(): Promise<AxiosResponse<unknown>> {
    try {
      const response = await axiosInstance.get(`/api/User/Logout`);
      return response;
    } catch (error) {
      console.error('Error: ', error);
      throw error;
    }
  },
  // --- Admin endpoints (require sysadmin privilege) ---
  async getRoles(): Promise<AxiosResponse<Role[]>> {
    try {
      const response = await axiosInstance.get(`/api/User/GetRoles`);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getAllUsers(): Promise<AxiosResponse<User[]>> {
    try {
      const response = await axiosInstance.get(`/api/User/GetAll`);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async getUserById(id: string | number): Promise<AxiosResponse<User>> {
    try {
      const response = await axiosInstance.get(`/api/User/${id}`);
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  async saveUser(user: User, password?: string): Promise<AxiosResponse<User>> {
    try {
      const params = password ? { password } : {};
      const response = await axiosInstance.post(`/api/User/SaveUser`, user, { params });
      return response;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  },
  // --- Impersonation (ADO #5044) — sysadmin starts, anyone with `login` stops / queries state ---
  async startImpersonation(targetUserId: number): Promise<AxiosResponse<AuthSessionResponse>> {
    try {
      const response = await axiosInstance.post(`/api/User/StartImpersonation`, { targetUserId });
      return response;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  },
  async stopImpersonation(): Promise<AxiosResponse<AuthSessionResponse>> {
    try {
      const response = await axiosInstance.post(`/api/User/StopImpersonation`);
      return response;
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      throw error;
    }
  },
  async getImpersonationState(): Promise<AxiosResponse<ImpersonationState>> {
    try {
      const response = await axiosInstance.get(`/api/User/ImpersonationState`);
      return response;
    } catch (error) {
      console.error('Error fetching impersonation state:', error);
      throw error;
    }
  },
};
